import React, { useRef, useState, useEffect } from 'react'
import $t from '../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import Card from '../../common/Card'
import Actions from '../../../actions'
import {
  isLaterAppointment,
  isNeedToAskMyPharmacyUpdate,
  isPendingAppointment,
  isQueueAppointment
} from '../../../utils/appointmentsUtils'
import { isNeedToAskEmergencyContactUpdate } from '../../../utils/getCareUtils'
import dayjs from 'dayjs'
import { Button } from 'antd'
import Page from '../../common/Page'
import ActionButton from '../../common/ActionButton/ActionButton'
import TimeSlotMessage from '../../common/TimeSlotMessage'
import { HMOService, clinicTypes, routes } from '../../../consts'
import Dialog from '../../common/Dialog'
import UploadDocumentsAction from '../../common/UploadDocumentsAction'
import config from '../../../config.json'
import { getIsHmoMember } from '../../../utils/hmoUtils'

import './BeforeVisitPage.css'

export const BeforeVisitPage = () => {
  const dispatch = useDispatch()
  const inputFile = useRef()

  const { isMinorApp, isFileUploaded, isFileUploadLoading } = useSelector(
    state => state.app
  )
  const isConnectivityTested = useSelector(
    state => state.getCare?.isConnectivityTested
  )
  const isEmergencyContactExist = useSelector(
    state => !isNeedToAskEmergencyContactUpdate(state)
  )
  const isPharmachExist = useSelector(
    state => !isNeedToAskMyPharmacyUpdate(state)
  )

  const userDetails = useSelector(state => state.profile?.userDetails)

  const [isVisitGuidelinesDisplay, setIsVisitGuidelinesDisplay] =
    useState(false)

  // Reset file input after successful upload
  useEffect(() => {
    if (isFileUploaded && inputFile?.current) {
      inputFile.current.value = ''
    }
  }, [isFileUploaded])

  const { selectedAppointment } = useSelector(state => state.appointments)
  const isHMOVisit =
    selectedAppointment?.antidote_clinic === clinicTypes.PRIMARY + HMOService
  const isFollowUp = !isHMOVisit && selectedAppointment?.is_clinic_follow_up
  const isHmoMember = getIsHmoMember(userDetails)

  const isAppointmentPending = isPendingAppointment(selectedAppointment)
  const isAppointmentInQueue = isQueueAppointment(selectedAppointment)
  const isAppointmentLater = isLaterAppointment(selectedAppointment)

  const isAppointmentLoading = useSelector(
    state => state.appointments.isAppointmentLoading
  )

  const isLoadingPage =
    !isAppointmentPending && !isAppointmentInQueue && !isAppointmentLater

  // todo: need to implement correct indication for followup intake
  // const navigateToFollowupIntake = () => {
  //   const clinicName = selectedAppointment?.antidote_clinic
  //   switch (clinicName) {
  //     case clinicTypes.DIABETES: {
  //       dispatch(Actions.createAction(Actions.OPEN_DM_FOLLOW_UP_BOT_SCREEN))
  //       break
  //     }
  //     case clinicTypes.HTN: {
  //       dispatch(Actions.createAction(Actions.OPEN_HTN_FOLLOW_UP_BOT_SCREEN))
  //       break
  //     }
  //     case clinicTypes.MENTAL: {
  //       dispatch(Actions.createAction(Actions.OPEN_MH_FOLLOW_UP_BOT_SCREEN))
  //       break
  //     }
  //     default:
  //       break
  //   }
  // }

  const beforeVisitIllustrator =
    config.gcpStorageUrl + '/img/Before_Desktop.svg'

  const beforeVisitActions = [
    // todo: need to implement correct indication for followup intake
    // {
    //   title: $t('beforeVisitPage.followupIntake'),
    //   onPress: () => navigateToFollowupIntake(),
    //   icon: isFollowupIntakeExist ? 'check' : 'check-circle-emply',
    //   hidden: !isFollowUp,
    //   iconSize: 12,
    //   className: isFollowupIntakeExist
    //     ? 'before-visit-action trackable checked'
    //     : 'before-visit-action trackable',
    // },
    {
      title: $t('beforeVisitPage.emergencyContact'),
      hidden: isMinorApp,
      onPress: () =>
        dispatch(
          Actions.createAction(Actions.UPDATE_EMERGENCY_CONTACT_PRESSED, {
            from: routes.BEFORE_VISIT
          })
        ),
      icon: isEmergencyContactExist ? 'check' : 'check-circle-emply',
      iconSize: 12,
      className: isEmergencyContactExist
        ? 'before-visit-action trackable checked'
        : 'before-visit-action trackable'
    },
    {
      title: $t('beforeVisitPage.selectedPharmacy'),
      hidden: isMinorApp,
      onPress: () =>
        dispatch(
          Actions.createAction(Actions.UPDATE_MY_PHARMACY_PRESSED, {
            from: routes.BEFORE_VISIT
          })
        ),
      icon: isPharmachExist ? 'check' : 'check-circle-emply',
      iconSize: 12,
      className: isPharmachExist
        ? 'before-visit-action trackable checked'
        : 'before-visit-action trackable'
    },
    {
      title: $t('beforeVisitPage.uploadFiles'),
      onPress: () => {
        dispatch(Actions.createAction(Actions.UPLOAD_FILE_PRESSED))
        if (inputFile?.current) inputFile.current.click()
      },
      icon: isFileUploaded ? 'check' : 'check-circle-emply',
      iconSize: 12,
      loading: isFileUploadLoading,
      className: isFileUploaded
        ? 'before-visit-action trackable checked'
        : 'before-visit-action trackable'
    },
    {
      title: $t('beforeVisitPage.connectivityCheck'),
      hidden: !isAppointmentInQueue,
      onPress: () =>
        dispatch(Actions.createAction(Actions.INITIATE_CONNECTIVITY_TEST)),
      icon: isConnectivityTested ? 'check' : 'check-circle-emply',
      iconSize: 12,
      className: isConnectivityTested
        ? 'before-visit-action trackable checked'
        : 'before-visit-action trackable'
    }
  ]

  const navigateHome = () => {
    dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT))
  }

  const navigatePayment = () => {
    dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PLANS))
  }

  const scheduleAnotherAppointment = () => {
    // clear family selection and start get care flow again
    dispatch(
      Actions.createAction(Actions.CHOOSE_FAMILY_MEMBER, { member: null })
    )
    // antidote clinic can be primary/primaryHMO
    // NO SPECIALTY SERVICES FOR HMO MEMBERS
    selectedAppointment?.antidote_clinic?.includes(clinicTypes.PRIMARY) ||
    isHmoMember
      ? dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.GET_CARE))
      : dispatch(Actions.createAction(Actions.SPECIALTY_SERVICES_CLICKED))
  }

  const getPendingAppointmentCard = () => {
    return (
      <Card className='pending-card'>
        <div className='pending-card-title'>
          {$t('beforeVisitPage.oneLastStepBeforeApproval')}
        </div>
        <div className='pending-slot'>
          {isFollowUp ? (
            $t('beforeVisitPage.appointmentPendingFollowup')
          ) : (
            <>
              {$t('beforeVisitPage.appointmentPendingSlot')}{' '}
              <TimeSlotMessage displayOnlyTime className='bold' />{' '}
              {$t('beforeVisitPage.untilCompletePayment')}
            </>
          )}
        </div>
        <Button
          type='primary'
          onClick={navigatePayment}
          className='trackable'
          data-name='navigate-payment'
        >
          {' '}
          {$t('common.proceed')}{' '}
        </Button>
        <Button
          onClick={navigateHome}
          className='trackable'
          data-name='navigate-home'
        >
          {' '}
          {$t('common.maybeLater')}
        </Button>
      </Card>
    )
  }

  const getApprovedAppointmentCard = () => {
    return (
      <Card className='approved-card eta-card'>
        <div className='eta'>
          {$t('common.eta')}:{' '}
          <TimeSlotMessage displayOnlyTime className='eta-timer' />
        </div>
        <div className='eta-sub'>
          {$t('beforeVisitPage.waitingTimeMayChange')}
        </div>
        <div className='eta-sub-bold'>{$t('beforeVisitPage.stayTuned')}</div>
      </Card>
    )
  }

  const getLaterAppointmentCard = () => {
    return (
      <Card
        className={
          isAppointmentPending ? 'approved-card pending' : 'approved-card'
        }
      >
        <div className='schedule'>{$t('beforeVisitPage.scheduleMeeting')}</div>
        <div className='schedule-sub'>
          {selectedAppointment
            ? dayjs(selectedAppointment?.schduled_time)
                .local()
                .format('DD MMM, h.mm A')
            : ''}
        </div>
      </Card>
    )
  }

  const getBeforeVisitActions = () => {
    const availableBeforeVisitActions = beforeVisitActions?.filter(
      action => !action.hidden
    )
    return (
      <div className='before-visit-actions'>
        <div className='before-visit-actions-header'>
          {$t('beforeVisitPage.fillDetails')}:
        </div>
        <Card className='before-visit-actions-card'>
          {availableBeforeVisitActions?.map((action, i) => (
            <ActionButton
              key={i}
              label={action?.title}
              iconName={action?.icon}
              size={action?.iconSize}
              className={action?.className}
              onPress={action?.onPress}
              displayArrow={true}
              loading={action?.loading}
            />
          ))}
          <div className='guide-text-container'>
            <div className='guide-text'>{$t('beforeVisitPage.reviewOur')} </div>
            <Button
              type='link'
              className='guide-text trackable'
              data-name='visit-guidelines'
              onClick={() => setIsVisitGuidelinesDisplay(true)}
            >
              {$t('beforeVisitPage.visitGuidelines')}
            </Button>
          </div>
        </Card>
        {!isMinorApp && (
          <Button
            type='link'
            className='another-app-btn trackable'
            onClick={scheduleAnotherAppointment}
            data-name='schedule-another-appointment'
          >
            {
              // antidote clinic can be primary/primaryHMO
              // NO SPECIALTY SERVICES FOR HMO MEMBERS
              selectedAppointment?.antidote_clinic?.includes(
                clinicTypes.PRIMARY
              ) || isHmoMember
                ? $t('beforeVisitPage.scheduleAnotherVisit')
                : $t('beforeVisitPage.scheduleAnotherService')
            }
          </Button>
        )}
      </div>
    )
  }

  const clinicName = $t(
    'clinicNames.' + selectedAppointment?.antidote_clinic
  ).toLowerCase()

  return (
    <Page
      className='before-visit-page grey'
      loading={isLoadingPage || isAppointmentLoading}
      hideAlertBanner
    >
      <div className='before-visit-content'>
        <div className='before-visit-img-container'>
          <img
            alt=''
            className='before-visit-img'
            src={beforeVisitIllustrator}
          />
          <div className='before-visit-img-background' />
        </div>
        <div className='appointment-title'>
          {isAppointmentPending
            ? $t('beforeVisitPage.pendingAppointmentTitle', {
                clinic: isHMOVisit ? '' : clinicName,
                followup: isFollowUp ? $t('beforeVisitPage.followup') : ''
              })
            : $t('beforeVisitPage.approveAppointmentTitle', {
                clinic: isHMOVisit ? '' : clinicName,
                followup: isFollowUp ? $t('beforeVisitPage.followup') : ''
              })}
        </div>
        {isAppointmentPending && (
          <div className='appointment-disclaimer'>
            {$t('beforeVisitPage.disclaimer')}
            <Button
              type='link'
              onClick={() =>
                dispatch(
                  Actions.createAction(
                    Actions.NAVIGATED_TO,
                    routes.MENU_ROUTE.MECICAL_VISITS_FUTURE
                  )
                )
              }
            >
              {$t('beforeVisitPage.viewVisitDetails')}
            </Button>
          </div>
        )}
        {!isLoadingPage && (
          <>
            {isAppointmentLater && getLaterAppointmentCard()}
            {isAppointmentInQueue && getApprovedAppointmentCard()}
            {isAppointmentPending && getPendingAppointmentCard()}
            {!isAppointmentPending && getBeforeVisitActions()}
          </>
        )}
        <Dialog
          title={$t('beforeVisitPage.guidelines.title')}
          isVisible={isVisitGuidelinesDisplay}
          footerActions={[
            <Button
              className='dialog-action'
              type='primary'
              onClick={() => setIsVisitGuidelinesDisplay(false)}
            >
              {$t('common.understand')}
            </Button>
          ]}
          onCancel={() => setIsVisitGuidelinesDisplay(false)}
          className='before-visit-dialog'
        >
          <div className='before-visit-content column light'>
            <div className='before-visit-body'>
              {$t('beforeVisitPage.guidelines.body')}
            </div>
            <div className='before-visit-note extra-light'>
              {$t('beforeVisitPage.guidelines.note')}
            </div>
          </div>
        </Dialog>
      </div>
      <UploadDocumentsAction ref={inputFile} />
    </Page>
  )
}

export default BeforeVisitPage
